import { Component, Injector } from '@angular/core';
import { StudyTitlesService, CMEAreaService, SmtService } from '../../ve.service';
import { BaseGridComponent } from '../base-grid/base-grid.component';
import { CgpConfirmationDialogService } from '../../../../shared/cgp-confirmation-dialog/cgp-confirmation-dialog.service';
import { CgpSnackbarService } from '../../../../shared/cgp-snackbar/cgp-snackbar.service';
import { Validators, UntypedFormGroup } from '@angular/forms';
import { FilterableService } from '../../filterable-service';

@Component({
  selector: 'app-study-titles',
  templateUrl: '../base-grid/base-grid.component.html',
})
export class StudyTitlesComponent extends BaseGridComponent {
  private readonly gaCMEService: CMEAreaService;
  private readonly gaSMTService: SmtService;
  private readonly cmeAreaService = new FilterableService();
  private readonly smt1Service = new FilterableService();
  private readonly smt2Service = new FilterableService();
  private readonly smt3Service = new FilterableService();

  public descMaxLength: number = null;

  constructor(
    cgpConfirmationDialogService: CgpConfirmationDialogService,
    cgpSnackbarService: CgpSnackbarService,
    service: StudyTitlesService,
    injector: Injector,
  ) {
    super(cgpConfirmationDialogService, cgpSnackbarService, service, injector);
    this.gaCMEService = injector.get(CMEAreaService);
    this.gaSMTService = injector.get(SmtService);
    this.descMaxLength = 50;
    this.appCardTitle = 'Study Titles';
    this.path = '/?activeOnly=false';

    this.columns = [
      {
        field: 'Description',
        title: 'Description',
      },
      {
        field: 'CMEAreaDescription',
        title: 'CME Area',
        type: 'dropdown',
        service: this.cmeAreaService,
        textField: 'Description',
        valueField: 'Id',
        name: 'CMEAreaId',
        refreshSecondaryData: this.populateCMEAreaList,
      },
      {
        field: 'SMT1Description',
        title: 'SMT 1',
        type: 'dropdown',
        service: this.smt1Service,
        textField: 'Description',
        valueField: 'Id',
        name: 'SMT1Id',
        valueChange: this.setSMT1,
        refreshSecondaryData: this.readSMT,
      },
      {
        field: 'SMT2Description',
        title: 'SMT 2',
        type: 'dropdown',
        service: this.smt2Service,
        textField: 'Description',
        valueField: 'Id',
        name: 'SMT2Id',
        valueChange: this.setSMT2,
        resetFilter: this.resetSMT2Filter,
      },
      {
        field: 'SMT3Description',
        title: 'SMT 3',
        type: 'dropdown',
        service: this.smt3Service,
        textField: 'Description',
        valueField: 'Id',
        name: 'SMT3Id',
        resetFilter: this.resetSMT3Filter,
      },
      {
        field: 'IncludeInTemplate',
        title: 'Available for Multiple Work Orders',
        editor: 'boolean',
        filter: 'boolean'
      },
      {
        field: 'IsActive',
        title: 'Active',
        editor: 'boolean',
        filter: 'boolean'
      },
    ];

    this.editForm = this.formBuilder.group({
      Id: [],
      Description: ['', Validators.required],
      CMEAreaId: ['', Validators.required],
      CMEAreaDescription: [],
      SMT1Id: ['', Validators.required],
      SMT1Description: [],
      SMT2Id: ['', Validators.required],
      SMT2Description: [],
      SMT3Id: ['', Validators.required],
      SMT3Description: [],
      IsActive: [],
      IncludeInTemplate: [],
      AuditDto: []
    });
  }

  cellClickHandler({ sender, rowIndex, columnIndex, dataItem, isEdited }) {
    // columnIndex = 5 is 'Include In Template' column
    if (!isEdited && columnIndex === 5) {
      sender.editCell(rowIndex, columnIndex, this.createFormGroup(dataItem));
    }
  }

  cellCloseHandler(args: any) {
    const { formGroup, sender, rowIndex } = args;
    if (!formGroup.valid) {
      // prevent closing the edited cell if there are invalid values.
      args.preventDefault();
    } else if (formGroup.dirty) {
      const isNew = false;
      this.saveHandler({ sender, rowIndex, formGroup, isNew });
    }
  }

  createFormGroup(dataItem: any): UntypedFormGroup {
    return this.formBuilder.group({
      Id: [dataItem.Id],
      Description: [dataItem.Description, Validators.required],
      CMEAreaId: [dataItem.CMEAreaId],
      CMEAreaDescription: [dataItem.CMEAreaDescription],
      SMT1Id: [dataItem.SMT1Id, Validators.required],
      SMT1Description: [dataItem.SMT1Description],
      SMT2Id: [dataItem.SMT2Id, Validators.required],
      SMT2Description: [dataItem.SMT2Description],
      SMT3Id: [dataItem.SMT3Id, Validators.required],
      SMT3Description: [dataItem.SMT3Description],
      IsActive: [dataItem.IsActive],
      IncludeInTemplate: [dataItem.IncludeInTemplate],
      AuditDto: [dataItem.AuditDto]
    });
  }

  populateCMEAreaList(dataItem: any) {
    dataItem.gaCMEService.read('?activeOnly=true')
      .then(result => {
        if (result.length > 0) {
          result.sort((a, b) => a.Description.localeCompare(b.Description));
        }
        dataItem.cmeAreaService.next(result);
      });
  }

  readSMT(dataItem: any) {
    dataItem.gaSMTService.read('?activeOnly=true')
      .then(result => {
        dataItem.populateSMT1(result);
        dataItem.populateSMT2(result);
        dataItem.populateSMT3(result);
      });
  }

  populateSMT1(result: any) {
    let smt1data = [];
    result.forEach(element => {
      if (element.SMTReferenceId == null) {
        smt1data.unshift(element);
      }
    });
    if (smt1data.length > 0) {
      const temp = smt1data
        .filter(item => item.Description)
        .sort((a, b) => a.Description.localeCompare(b.Description));
      smt1data = temp;
    }
    this.smt1Service.next(smt1data);
  }

  populateSMT2(result: any) {
    let smt2data = result;
    if (smt2data.length > 0) {
      const temp = smt2data.filter(item => item.Description);
      smt2data = temp.sort((a, b) => a.Description.localeCompare(b.Description));
    }
    this.smt2Service.next(smt2data);
  }

  populateSMT3(result: any) {
    let smt3data = result;
    if (smt3data.length > 0) {
      const temp = smt3data.filter(item => item.Description);
      smt3data = temp.sort((a, b) => a.Description.localeCompare(b.Description));
    }
    this.smt3Service.next(smt3data);
  }

  // Filter SMT2
  setSMT1(event, dataItem) {
    if (event === null) {
      dataItem.editForm.get('SMT2Id').disable();
      dataItem.editForm.get('SMT2Id').reset();
    } else {
      const filteredSmt2Values = dataItem.gaSMTService.value.filter(x => x.SMTReferenceId === event);
      dataItem.smt2Service.next(filteredSmt2Values);
      dataItem.editForm.get('SMT2Id').enable();
      dataItem.editForm.get('SMT2Id').reset();
    }
    dataItem.editForm.get('SMT3Id').disable();
    dataItem.editForm.get('SMT3Id').reset();
  }

  // Filter SMT3
  setSMT2(event, dataItem) {
    if (event === null) {
      dataItem.editForm.get('SMT3Id').disable();
      dataItem.editForm.get('SMT3Id').reset();
    } else {
      const filteredSmt3Values = dataItem.gaSMTService.value.filter(x => x.SMTReferenceId === event);
      dataItem.smt3Service.next(filteredSmt3Values);
      dataItem.editForm.get('SMT3Id').enable();
      dataItem.editForm.get('SMT3Id').reset();
    }
  }

  resetSMT2Filter(event, dataItem) {
    const filteredSmt2Values = dataItem.gaSMTService.value.filter(x => x.SMTReferenceId === event);
    dataItem.smt2Service.next(filteredSmt2Values);
  }

  resetSMT3Filter(event, dataItem) {
    const filteredSmt3Values = dataItem.gaSMTService.value.filter(x => x.SMTReferenceId === event);
    dataItem.smt3Service.next(filteredSmt3Values);
  }
}
